<template>
  <div class="responsive-table" :class="{ 'table-expanded': tableExpanded }">
    <slot name="info"></slot>
    <!-- Frozen Table Heading Specific -->
    <div class="frozen-table-header" v-if="frozenHeader" :style="{ width: tableWidth + 'px' }">
      <div v-if="!disableExpand" class="my-2 text-right">
        <button class="btn btn-primary" type="button"
        v-if="!tableExpanded" @click="expandTable()">
          <span class="fa fa-fw fa-expand"></span>
        </button>
        <button class="btn btn-primary" type="button"
        v-if="tableExpanded" @click="closeTable()">
          <span class="fa fa-fw fa-compress"></span>
        </button>
        <button class="btn btn-primary" type="button"
        v-if="table && !tableExpanded" @click="toggleFrozenHeader()">
          <span class="fa fa-fw fa-caret-square-down"></span> Headings
        </button>
      </div>
      <table class="mb-0 table table-bordered table-striped table-hover table-sm text-center">
        <thead class="thead-light">
          <tr>
            <th v-for="(column, iColumn) in table.columns" :key="iColumn" scope="col" :width="(column.span * 100) / 12 + '%'">
              <div v-if="column.sort" @click="changeSort(column)">
                <strong class="col-header-sort" :class="{ 'active': column.name === sortColumn }">
                  {{ column.title }}
                </strong>
                <span class="fa" :class="{
                  'fa-angle-down': sortDirection.includes('DESC') || column.name !== sortColumn,
                  'fa-angle-up': sortDirection.includes('ASC')
                }"></span>
              </div>
              <div v-else>
                <strong>{{ column.title }}</strong>
              </div>
            </th>
          </tr>
        </thead>
      </table>
    </div>
    <div class="text-right" v-if="!disableExpand && !frozenHeader">
      <button class="btn btn-primary" type="button"
      v-if="!tableExpanded" @click="expandTable()">
        <span class="fa fa-fw fa-expand"></span>
      </button>
      <button class="btn btn-primary" type="button"
      v-if="tableExpanded" @click="closeTable()">
        <span class="fa fa-fw fa-compress"></span>
      </button>
      <button class="btn btn-primary" type="button"
      v-if="table && !tableExpanded" @click="toggleFrozenHeader()">
        <span class="fa fa-fw fa-caret-square-up"></span> Headings
      </button>
    </div>
    <h5 v-if="!hideTitle">
      {{ table.title }} <span class="badge badge-info" v-if="table.count">{{ table.count }}</span>
    </h5>
    <table class="mt-2 table table-bordered table-striped table-hover table-sm text-center" ref="table">
      <!-- Column Headers -->
      <thead class="thead-light" :style="{ 'visibility': !frozenHeader ? 'visible' : 'collapse' }">
        <tr>
          <th v-for="(column, iColumn) in table.columns" :key="iColumn" scope="col" :width="(column.span * 100) / 12 + '%'">
            <div v-if="column.sort" @click="changeSort(column)">
              <strong class="col-header-sort" :class="{ 'active': column.name === sortColumn }">
                {{ column.title }}
              </strong>
              <span class="fa" :class="{
                'fa-angle-down': sortDirection.includes('DESC') || column.name !== sortColumn,
                'fa-angle-up': sortDirection.includes('ASC')
              }"></span>
            </div>
            <div v-else>
              <strong>{{ column.title }}</strong>
            </div>
          </th>
        </tr>
      </thead>
      <!-- Rows -->
      <tbody>
        <slot name="rows">
          <template v-for="(row, i) in table.rows">
            <!-- Group Header -->
            <!-- i + 'header' concat to make key unique to normal rows -->
            <tr v-if="table.group" :key="i + 'header'">
              <th scope="row" v-if="i === 0 || row[table.group] != table.rows[i-1][table.group]"
                :key="`${i}-header`" colspan="12" class="bg-primary text-white">
                <strong class="text-big">
                  <span class="fa fa-fw" :class="`fa-${table.groupIcon}`"></span>
                  {{ row[table.group] }}
                </strong>
              </th>
            </tr>
            <!-- Normal Rows -->
            <tr :key="i">
              <template v-for="(column, iColumn) in table.columns">
                <td :width="(column.span * 100) / 12 + '%'" :key="iColumn">
                  <div class="text-muted" v-if="tableExpanded">{{ column.title }}</div>
                  <template v-if="column.type === 'link'">
                    <template v-if="row[column.name].target">
                      <a :href="row[column.name].link" :target="row[column.name].target">
                        {{ row[column.name].label }}
                      </a>
                    </template>
                    <template v-else-if="row[column.name].link">
                      <router-link :to="{ path: row[column.name].link }">
                        {{ row[column.name].label }}
                      </router-link>
                    </template>
                    <template v-else>
                      <span>{{ row[column.name].label }}</span>
                    </template>
                  </template>
                  <template v-if="column.type === 'date'">
                    {{ row[column.name].date | date_format(row[column.name].timezone) }}
                  </template>
                  <template v-if="column.type === 'shortDate'">
                    {{ row[column.name] | short_date_format }}
                  </template>
                  <template v-if="column.type === 'text'">
                    {{ row[column.name] }}
                  </template>
                  <template v-if="column.type === 'currency'">
                    {{ row[column.name] | to_dollar_amount_neg }}
                  </template>
                  <template v-if="column.type === 'button'">
                    <router-link v-if="row[column.name].link" :class="`btn-${row[column.name].buttonType}`" class="btn btn-small interactive-cursor" :to="{ path: row[column.name].link }">
                      <span v-if="row[column.name].icon" :class="`fa ${row[column.name].icon} fa-fw`"></span> {{ row[column.name].buttonText }}
                    </router-link>
                    <button v-if="row[column.name].functionName" :class="`btn-${row[column.name].buttonType}`" class="btn btn-small" @click="$dispatch(row[column.name].functionName, row[column.name].functionParams)">
                      <span v-if="row[column.name].icon" :class="`fa ${row[column.name].icon} fa-fw`"></span> {{ row[column.name].buttonText }}
                    </button>
                    <span v-if="row[column.name].labelText" :class="`badge-${row[column.name].labelType}`" class="badge">
                      {{ row[column.name].labelText }}
                    </span>
                  </template>
                  <template v-if="column.type === 'label' && row[column.name].labelText">
                    <span :class="`badge-${row[column.name].labelType}`" class="badge">
                      {{ row[column.name].labelText }}
                    </span>
                  </template>
                  <template v-if="column.type === 'toggle'">
                    <div class="text-center">
                      <checkbox-input
                      :options="{ fullWidth: true }"
                      v-model="row[column.name]"
                      @input="$emit('toggle', { row, name: column.name })">
                    </checkbox-input>
                    </div>
                  </template>
                </td>
              </template>
            </tr>
          </template>
        </slot>
      </tbody>
    </table>
  </div>
</template>

<script>
import CheckboxInput from '@/components/inputs/CheckboxInput'

export default {
  name: 'ResponsiveTable',
  components: {
    CheckboxInput
  },
  props: {
    table: {
      type: Object,
      default () {
        return {
          title: 'Table',
          count: 0,
          columns: [
            { title: 'ID', span: 4, name: 'id' },
            { title: 'Data', span: 4, name: 'data' },
            { title: 'Status', span: 4, name: 'status' }
          ],
          rows: [
            { id: 'reservation', name: 'Data', status: '--' }
          ],
          group: false,
          groupIcon: 'map'
        }
      }
    },
    disableExpand: {
      type: Boolean,
      default () {
        return false
      }
    },
    hideTitle: {
      type: Boolean,
      default () {
        return false
      }
    },
    defaultSort: {
      type: [String, Boolean],
      default () {
        return 'ASC'
      }
    },
    defaultSortColumn: {
      type: [String, Boolean],
      default () {
        return 'id'
      }
    }
  },
  data () {
    return {
      frozenHeader: false,
      tableExpanded: false,
      sortColumn: 'id',
      sortDirection: 'ASC',
      tableWidth: 0,
      windowResized: function (e) {
        // event for window resizing with fixed header
        this.tableWidth = this.$refs.table.clientWidth
      }.bind(this)
    }
  },
  mounted () {
    let defaultColumn = false
    if (this.table.columns) {
      defaultColumn = this.table.columns.find(el => el.defaultSort)
    }
    this.sortColumn = this.defaultSortColumn ? this.defaultSortColumn : (defaultColumn ? defaultColumn.name : '')
    this.sortDirection = this.defaultSort ? this.defaultSort : (defaultColumn ? defaultColumn.defaultDirection : '')
    this.tableWidth = this.$refs.table.clientWidth
  },
  methods: {
    expandTable () {
      this.tableExpanded = true
      if (this.frozenHeader) this.toggleFrozenHeader()
    },
    closeTable () {
      this.tableExpanded = false
    },
    toggleFrozenHeader () {
      this.tableWidth = this.$refs.table.clientWidth
      this.frozenHeader = !this.frozenHeader
      if (this.frozenHeader) {
        window.addEventListener('resize', this.windowResized)
      } else {
        window.removeEventListener('resize', this.windowResized)
      }
    },
    changeSort (column) {
      if (this.sortColumn === column.name) {
        this.sortDirection = this.sortDirection.includes('ASC') ? 'DESC NULLS LAST' : 'ASC NULLS LAST'
      } else {
        this.sortDirection = 'ASC NULLS LAST'
      }
      this.sortColumn = column.name
      this.$emit('orderColumn', { sortDirection: this.sortDirection, ...column })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/ResponsiveTable.vue